import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Programs", path: "/programs" },
    { name: "Gallery", path: "/gallery" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-black/90 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="group" data-testid="navbar-logo">
            <span className="font-display text-2xl text-white tracking-wide" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              CAMP CORMIER
            </span>
            <span className="block text-[10px] tracking-[0.4em] text-[#D4AF37] uppercase">
              Academy
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8 lg:gap-10">
            {navLinks.map((link) => {
              const active = location.pathname === link.path || (link.accent && location.pathname.startsWith("/recipes"));
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative text-sm tracking-wider uppercase transition-colors duration-300 flex items-center gap-1.5 whitespace-nowrap ${
                    active
                      ? "text-[#D4AF37]"
                      : link.accent
                      ? "text-[#D4AF37]/90 hover:text-[#D4AF37]"
                      : "text-white/70 hover:text-white"
                  }`}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {link.icon && <span aria-hidden="true">{link.icon}</span>}
                  {link.name}
                  {link.accent && (
                    <span className="absolute -top-2 -right-7 bg-[#D4AF37] text-black text-[8px] font-bold tracking-wider px-1.5 py-0.5 rounded-sm">
                      NEW
                    </span>
                  )}
                </Link>
              );
            })}
            
            <Link
              to="/assessment"
              className="bg-[#D4AF37] text-black px-6 py-2.5 text-sm tracking-wider uppercase font-semibold hover:bg-white transition-colors duration-300"
              data-testid="nav-apply-btn"
            >
              Apply Now
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-white p-2"
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/5">
          <div className="px-6 py-8 space-y-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`flex items-center gap-2 text-lg tracking-wider uppercase ${
                  location.pathname === link.path || (link.accent && location.pathname.startsWith("/recipes"))
                    ? "text-[#D4AF37]"
                    : link.accent
                    ? "text-[#D4AF37]/90"
                    : "text-white/70"
                }`}
                data-testid={`nav-mobile-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {link.icon && <span aria-hidden="true">{link.icon}</span>}
                {link.name}
                {link.accent && (
                  <span className="bg-[#D4AF37] text-black text-[9px] font-bold tracking-wider px-1.5 py-0.5">
                    NEW
                  </span>
                )}
              </Link>
            ))}
            <Link
              to="/assessment"
              onClick={() => setIsOpen(false)}
              className="block w-full text-center bg-[#D4AF37] text-black py-3 text-sm tracking-wider uppercase font-semibold"
            >
              Apply Now
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
